import { jsPDF } from "jspdf";
import moment from "moment-timezone";
import QRCode from "qrcode";
import React from "react";
import { languages } from "../../constants/languages";

export const exportRefereeQRCodes = async (tournament) => {
  const generateQRCode = async (url) => {
    try {
      const qrDataUrl = await QRCode.toDataURL(url, {
        margin: 1,
        width: 200,
      });
      return qrDataUrl;
    } catch (err) {
      console.error(err);
      return null;
    }
  };

  const truncateRefereeName = (name) => {
    return name.length > 30 ? name.substring(0, 30) + "..." : name;
  };

  const addPageHeader = (
    doc,
    pageNum,
    totalPages,
    pageWidth,
    marginVertical
  ) => {
    // Add title with tournament name
    doc.setFontSize(14);
    let titleText = tournament.name;
    if (tournament.useJudges) {
      titleText += " — " + languages.referees.judges;
    } else {
      titleText += " — " + languages.schedule.referees;
    }
    titleText += ` (${pageNum}/${totalPages})`;
    const titleWidth =
      (doc.getStringUnitWidth(titleText) * 14) / doc.internal.scaleFactor;
    const titleX = (pageWidth - titleWidth) / 2;
    doc.text(titleText, titleX, marginVertical + 10);
  };

  try {
    // Get all referees
    const refereesSnapshot = await window.firebase
      .firestore()
      .collection("tournaments")
      .doc(tournament[".key"])
      .collection("referees")
      .get();

    const referees = [];
    refereesSnapshot.forEach((doc) => {
      let referee = doc.data();
      referee["id"] = doc.id;
      if (referee.createdAt) {
        referee["added"] = moment(referee.createdAt.toDate()).unix();
      }
      referees.push(referee);
    });

    // Get all referee codes
    const codesSnapshot = await window.firebase
      .firestore()
      .collection("tournaments")
      .doc(tournament[".key"])
      .collection("teamCodes")
      .where("type", "==", "referee")
      .get();

    const codes = {};
    codesSnapshot.forEach((doc) => {
      codes[doc.data().teamId] = doc.id;
    });

    // Generate PDF
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();

    const marginHorizontal = 40; // Left and right margins
    const marginVertical = 15; // Top and bottom margins
    const qrSize = 45;
    const qrPerRow = 2;
    const qrSpacing =
      (pageWidth - 2 * marginHorizontal - qrSize * qrPerRow) / (qrPerRow - 1);
    const qrWithLabelHeight = qrSize + 15;
    const qrPerPage = 8;

    // Sort referees alphabetically
    const sortedReferees = [...referees].sort((a, b) => {
      if (a.name < b.name) return -1;
      if (a.name > b.name) return 1;
      return 0;
    });

    // Calculate total pages
    const refereesWithCodes = sortedReferees.filter(
      (referee) => codes[referee.id]
    );
    const totalPages = Math.ceil(refereesWithCodes.length / qrPerPage);
    let currentPage = 1;

    // Reset positions
    let currentY = marginVertical + 25;
    let currentX = marginHorizontal;
    let count = 0;

    // Add header for first page
    addPageHeader(doc, currentPage, totalPages, pageWidth, marginVertical);

    // Process referees
    for (const referee of refereesWithCodes) {
      if (codes[referee.id]) {
        const loginUrl =
          "https://" +
          (tournament.isPSD || tournament.crqpsd
            ? "tournify.prosoccerdata.com/"
            : "tournifyapp.com/live/") +
          tournament.liveLink +
          "/referee/" +
          referee.id +
          "/" +
          codes[referee.id];

        const qrDataUrl = await generateQRCode(loginUrl);

        if (qrDataUrl) {
          // Add new page if needed
          if (count > 0 && count % qrPerPage === 0) {
            doc.addPage();
            currentPage++;
            currentX = marginHorizontal;
            currentY = marginVertical + 25;
            addPageHeader(
              doc,
              currentPage,
              totalPages,
              pageWidth,
              marginVertical
            );
          }

          // Add QR code
          doc.addImage(qrDataUrl, "PNG", currentX, currentY, qrSize, qrSize);

          // Add referee name below QR code
          doc.setFontSize(10);
          const truncatedName = truncateRefereeName(referee.name);
          const textWidth =
            (doc.getStringUnitWidth(truncatedName) * 10) /
            doc.internal.scaleFactor;
          const textX = currentX + (qrSize - textWidth) / 2;
          doc.text(truncatedName, textX, currentY + qrSize + 4);

          // Update position
          if (count % qrPerRow === qrPerRow - 1) {
            currentX = marginHorizontal;
            currentY += qrWithLabelHeight;
          } else {
            currentX += qrSize + qrSpacing;
          }

          count++;
        }
      }
    }

    // Save the PDF
    const title = tournament.useJudges
      ? languages.referees.judgesLow
      : languages.schedule.refereesLow;
    doc.save(
      languages.teams.QRcodes + " " + title + " - " + tournament.name + ".pdf"
    );
  } catch (error) {
    console.error("Error generating PDF:", error);
  }
};

// Wrapper component for backward compatibility
const ExportRefereeQRCodesContainer = ({ tournament, autoExport }) => {
  React.useEffect(() => {
    if (autoExport) {
      exportRefereeQRCodes(tournament);
    }
  }, [autoExport, tournament]);

  return null;
};

export default ExportRefereeQRCodesContainer;
